import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { useNextStep } from "nextstepjs";
import { useTourLayoutRefresh } from "@/components/nextstep-bridge";
import { useState, useEffect } from "react";
import {
  useSquadListInfinite,
  useStats,
  queryClient,
} from "@/lib/hooks/useApi";
import useInfiniteScroll from "@/lib/use-infinite-scroll";
import { SquadList } from "@/components/SquadList";
import { CreateSquadSheet } from "@/components/CreateSquadSheet";
import { DeleteSquadSheet } from "@/components/DeleteSquadSheet";
import { DefaultButton } from "@/components/DefaultButton";
import { LoginGate } from "@/components/LoginGate";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";

import { useTranslation } from "@/lib/hooks/useTranslation";

export const Route = createFileRoute("/(game)/squad")({
  component: SquadPage,
});

function SquadPage() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { t } = useTranslation();
  const { startNextStep, closeNextStep, currentTour, currentStep, setCurrentStep } =
    useNextStep();
  const { refreshTourLayout, setSuspendTour } = useTourLayoutRefresh();

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSquadIds, setSelectedSquadIds] = useState<string[]>([]);

  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");
  const userId = statsData?.userId;

  const {
    data: squadData,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSquadListInfinite(userId, {
    squadName: appliedSearch || undefined,
    limit: 10,
  });

  const squadList = squadData?.pages.flatMap((page) => page.squads) ?? [];

  const queryKey = [
    "squads",
    "infinite",
    userId,
    appliedSearch || undefined,
    10,
  ];

  const { lastElementRef } = useInfiniteScroll({
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchTerm.trim();
      setAppliedSearch(trimmed.length >= 2 ? trimmed : "");
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Resume lobbySquadTour step 2 after navigating from /home
  // Suspend first to prevent step 0 flicker, then jump directly to step 2
  useEffect(() => {
    const pending = sessionStorage.getItem("cfl:lobby-squad-tour-step2");
    if (!pending) return;
    sessionStorage.removeItem("cfl:lobby-squad-tour-step2");
    setSuspendTour(true);
    setTimeout(() => {
      startNextStep("lobbySquadTour");
      setCurrentStep(2);
      setSuspendTour(false);
      refreshTourLayout();
      const btn = document.querySelector("[data-step='create-squad-button']");
      btn?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
  }, [startNextStep, setCurrentStep, refreshTourLayout, setSuspendTour]);

  return (
    <div className="bg-[#1D2A52] flex flex-1 flex-col h-full overflow-hidden pb-12">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-2">
        <div />
        <div>
          {isEditMode ? (
            <img
              src="https://cdn.navalabs.io/assets/icon_check_JVuGuy.png"
              alt={t("basecamp.checkAlt")}
              className="size-[25px] cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-95 transition-all duration-100"
              onClick={() => {
                setIsEditMode(false);
                setSelectedSquadIds([]);
              }}
            />
          ) : (
            <img
              src="https://cdn.navalabs.io/assets/settings_button_pjTONX.png"
              alt={t("basecamp.settingsAlt")}
              className="size-[25px] cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-95 transition-all duration-100"
              onClick={() => setIsEditMode(true)}
            />
          )}
        </div>
      </div>

      {/* Squad List */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-2">
        <SquadList
          squadList={squadList}
          isEditMode={isEditMode}
          isSquadListLoading={isLoading}
          onSquadRenamed={refetch}
          selectedSquadIds={selectedSquadIds}
          onToggleSquadSelection={(id) =>
            setSelectedSquadIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
            )
          }
          searchQuery={searchTerm}
          onSearchQueryChange={setSearchTerm}
          shouldShowSearchBar={squadList.length > 0}
          refetch={refetch}
          queryKey={queryKey.map(String)}
          lastElementRef={lastElementRef}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      {/* Bottom Action */}
      <div className="flex flex-col gap-2 shrink-0 p-2 pb-16">
        {isEditMode && selectedSquadIds.length > 0 ? (
          <DeleteSquadSheet
            squadIds={selectedSquadIds}
            onSquadDeleted={() => {
              setSelectedSquadIds([]);
              setIsEditMode(false);
              queryClient.resetQueries({
                queryKey: ["squads", "infinite"],
              });
            }}
          >
            <DefaultButton variant="red" className="uppercase h-14! text-lg">
              {t("basecamp.deleteSelected", {
                count: selectedSquadIds.length,
              })}
            </DefaultButton>
          </DeleteSquadSheet>
        ) : (
          <CreateSquadSheet onSquadCreated={refetch}>
            <LoginGate unauthenticatedLabel={t("basecamp.loginToCreateSquad")}>
              <DefaultButton
                data-step="create-squad-button"
                variant="green"
                className="uppercase h-14! text-lg"
                onClickCapture={() => {
                  if (currentTour === "lobbySquadTour") {
                    closeNextStep();
                    localStorage.setItem("cfl:lobby-squad-tour-done", "true");
                  }
                }}
              >
                {t("basecamp.createSquad")}
              </DefaultButton>
            </LoginGate>
          </CreateSquadSheet>
        )}
      </div>

    </div>
  );
}
